import { isValidAddress } from '@/lib/address/validator';
import { addressFormat } from '@/lib/address/formatter';
import { Network } from '@/Models/Network';

export type AddressDisplayFormat = 'short' | 'ending' | 'full' | 'emphasized';

export interface AddressFormatOptions {
  format?: AddressDisplayFormat;
  maxNameLength?: number; // For emails
}

/**
 * Immutable Address class that encapsulates address strings with network context.
 * Provides consistent formatting, validation, and display methods.
 *
 * @example
 * ```typescript
 * const addr = new Address('0x1234...', network);
 * addr.toShortString(); // "0x123...5678"
 * Address.isValid('0x1234...', { name: 'ETHEREUM_MAINNET' }); // true
 * ```
 */
export class Address {
  private readonly _raw: string;
  private readonly _normalized: string;
  private readonly _network: Network | null | undefined;

  /**
   * Creates a new Address instance with network context
   * @param address - The raw address string
   * @param network - Network context for network-specific formatting
   * @param providerName - Optional provider name for additional context
   */
  constructor(address: string, network: Network, providerName?: string);

  /**
   * Creates a new Address instance with provider name only
   * @param address - The raw address string
   * @param network - Must be null or undefined when using providerName alone
   * @param providerName - Provider name for provider-specific formatting
   */
  constructor(address: string, network: null | undefined, providerName: string);

  /**
   * Creates a new Address instance with optional network and required provider name
   * Used when network may or may not be available but provider name is known
   * @param address - The raw address string
   * @param network - Optional network context (can be null or undefined)
   * @param providerName - Provider name for provider-specific formatting
   */
  constructor(address: string, network: Network | null | undefined, providerName: string);

  constructor(address: string, network: Network | null | undefined, providerName?: string) {
    if (!network && !providerName) {
      throw new Error('Address requires either network or providerName');
    }

    this._raw = address || '';
    this._network = network;

    try {
      this._normalized = addressFormat({ address: this._raw, network, providerName });
    } catch {
      this._normalized = this._raw;
    }
  }

  /**
   * Get the raw, unmodified address string
   */
  get raw(): string {
    return this._raw;
  }

  /**
   * Get the normalized address (formatted for the network)
   */
  get normalized(): string {
    return this._normalized;
  }

  /**
   * Get the full address
   */
  get full(): string {
    return this._normalized;
  }


  /**
   * Get the associated network
   */
  get network(): Network | null | undefined {
    return this._network;
  }

  /**
   * Check if this address is valid for its network
   */
  static isValid(address: string, network: Network | null = null): boolean {
    try {
      return isValidAddress(address, network);
    }
    catch {
      return false
    }
  }

  /**
   * Format address as shortened display: first5...last4
   * @returns Shortened address (e.g., "0x123...5678")
   */
  toShortString(): string {
    const addr = this._normalized;

    if (!addr || addr.length < 13) {
      return this.full;
    }

    const shortened = `${addr.substring(0, 5)}...${addr.substring(addr.length - 4)}`;
    return shortened;
  }

  /**
   * Format address as ending only: ...last4
   * @returns Last 4 characters with ellipsis (e.g., "...5678")
   */
  toEndingString(): string {
    const addr = this._normalized;
    if (!addr) return '';
    return `...${addr.substring(addr.length - 4)}`;
  }

  /**
   * Get address parts for emphasized display (bold first4 and last4)
   * Used for rendering with different styles for start/middle/end
   * @returns Object with start, middle, end parts
   */
  toEmphasizedParts(): { start: string; middle: string; end: string } {
    const addr = this._normalized;
    if (!addr || addr.length <= 8) {
      return { start: addr, middle: '', end: '' };
    }

    return {
      start: addr.slice(0, 5),
      middle: addr.slice(5, -4),
      end: addr.slice(-4)
    };
  }

  /**
   * Convert to string (default: full format)
   */
  toString(): string {
    return this.full;
  }

  /**
   * Static method to compare two address strings with network context
   * More efficient than creating Address instances when you just need comparison
   * @param addr1 - First address string
   * @param addr2 - Second address string
   * @param network - Optional network context for both addresses
   * @param providerName - Optional provider name for both addresses
   * @returns true if addresses are equivalent after normalization
   */
  static equals(
    addr1: string,
    addr2: string,
    network?: Network | null,
    providerName?: string
  ): boolean {
    if (!addr1 || !addr2) return false;

    try {
      const norm1 = addressFormat({ address: addr1, network, providerName });
      const norm2 = addressFormat({ address: addr2, network, providerName });
      return norm1 === norm2;
    } catch {
      return addr1 === addr2;
    }
  }

}
